import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { Users, Briefcase, Search, Home, User, LogOut, Plus, X, Menu } from "lucide-react";
import { SkillsSelector } from "@/components/SkillsSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to include credentials (cookies)
axios.defaults.withCredentials = true;

// Helper function to get match badge color based on percentage
function getMatchBadgeColor(percentage) {
  if (percentage >= 80) return 'bg-green-600 text-white';
  if (percentage >= 60) return 'bg-amber-500 text-white';
  return 'bg-slate-500 text-white';
}

// Auth Context
const AuthContext = React.createContext(null);

function useAuth() {
  return React.useContext(AuthContext);
}

// Landing Page
function LandingPage() {
  const navigate = useNavigate();
  
  const handleLogin = () => {
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-12">
        <nav className="flex justify-between items-center mb-16">
          <div className="flex items-center gap-3">
            <img src="/logos/badge-logo.png" alt="Asymbly Logo" className="h-12 w-auto" />
            <h1 className="text-3xl font-bold text-slate-800">Asymbly</h1>
          </div>
          <Button onClick={handleLogin} size="lg" data-testid="login-button" className="bg-brand-navy hover:bg-slate-800 text-white">
            Sign In
          </Button>
        </nav>
        
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Connect Political Talent<br />with Opportunity
            </h2>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              The professional network for the political community. Find your perfect match,
              build meaningful connections, and power the next great campaign.
            </p>
            <Button onClick={handleLogin} size="lg" className="text-lg px-8 py-6 bg-brand-navy hover:bg-slate-800 text-white" data-testid="get-started-button">
              Get Started
            </Button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card data-testid="feature-matching">
              <CardHeader>
                <Users className="w-12 h-12 mb-4 text-brand-teal" />
                <CardTitle>AI-Powered Matching</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Our Vibe Check questionnaire uses AI to find your ideal political collaborators
                  based on values, work style, and goals.
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="feature-campaigns">
              <CardHeader>
                <Briefcase className="w-12 h-12 mb-4 text-brand-teal" />
                <CardTitle>Campaign Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Create and discover campaign opportunities at local, state, and federal levels.
                  Build your team and make an impact.
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="feature-network">
              <CardHeader>
                <Users className="w-12 h-12 mb-4 text-brand-teal" />
                <CardTitle>Professional Network</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Connect with individuals, candidates, and build a network that shares your
                  political values and vision for the future.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="bg-brand-navy rounded-2xl p-12 shadow-lg">
            <h3 className="text-3xl font-bold text-white mb-4 text-center">
              Trusted by Political Professionals
            </h3>
            <p className="text-center text-white/90 mb-8">
              Join the movement to make political networking more efficient, trustworthy, and impactful.
            </p>
            <div className="flex justify-center">
              <Button onClick={handleLogin} size="lg" data-testid="join-button" className="bg-brand-teal hover:bg-brand-teal-dark text-white">
                Join Asymbly
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Email/Password Login Page
function EmailLoginPage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState(''); // For development display
  
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email,
        password,
        name,
        user_type: 'individual'
      });
      
      toast.success('Account created! Please log in.');
      setMode('login');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      // Store code for development display
      setDevCode(response.data.code || '');
      
      toast.success('Verification code sent to your email!');
      setMode('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/verify-login`, {
        email,
        code
      });
      
      toast.success('Login successful!');
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-4 justify-center">
            <img src="/logos/badge-logo.png" alt="Asymbly Logo" className="h-16 w-auto" />
            <h1 className="text-3xl font-bold text-slate-800">Asymbly</h1>
          </div>
          <CardTitle className="text-center">
            {mode === 'signup' ? 'Create Account' : mode === 'verify' ? 'Verify Email' : 'Sign In'}
          </CardTitle>
          <CardDescription className="text-center">
            {mode === 'signup' ? 'Join the political professional network' : 
             mode === 'verify' ? 'Enter the code sent to your email' : 
             'Welcome back!'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-2"
                  minLength={6}
                />
              </div>
              
              {error && <p className="text-sm text-red-600">{error}</p>}
              
              <Button type="submit" className="w-full bg-brand-navy hover:bg-slate-800 text-white" disabled={loading}>
                {loading ? 'Creating Account...' : 'Sign Up'}
              </Button>
              
              <p className="text-center text-sm text-slate-600">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setError('');
                  }}
                  className="text-slate-800 font-medium hover:underline"
                >
                  Sign In
                </button>
              </p>
            </form>
          )}
          
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-2"
                />
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-slate-600 hover:text-slate-800 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
              
              {error && <p className="text-sm text-red-600">{error}</p>}
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Signing In...' : 'Sign In with Email'}
              </Button>
              
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-slate-500">Or</span>
                </div>
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 mr-2" />
                Continue with Google
              </Button>
              
              <p className="text-center text-sm text-slate-600">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('signup');
                    setError('');
                  }}
                  className="text-slate-800 font-medium hover:underline"
                >
                  Sign Up
                </button>
              </p>
            </form>
          )}
          
          {mode === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              {devCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-amber-800 font-medium mb-2">🔧 DEVELOPMENT MODE</p>
                  <p className="text-sm text-amber-900 mb-1">Your verification code:</p>
                  <p className="text-3xl font-mono font-bold text-amber-900 tracking-widest text-center">
                    {devCode}
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    (This display is for testing only - will be removed in production)
                  </p>
                </div>
              )}
              
              <div>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  required
                  className="mt-2 text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  {devCode ? 'Enter the code shown above' : 'Check your email for the 6-digit code'}
                </p>
              </div>
              
              {error && <p className="text-sm text-red-600">{error}</p>}
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              
              <p className="text-center text-sm text-slate-600">
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setCode('');
                    setDevCode('');
                    setError('');
                  }}
                  className="text-slate-800 font-medium hover:underline"
                >
                  ← Back to Login
                </button>
              </p>
            </form>
          )}
          
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-slate-600 hover:text-slate-800"
            >
              ← Back to Home
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Auth Handler
function AuthHandler() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        setProcessing(true);
        try {
          // Create session on backend
          await axios.post(`${API}/auth/session`, null, {
            headers: { 'X-Session-ID': sessionId }
          });
          
          // Clear hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          // Refresh auth state
          await checkAuth();
          
          // Check if profile exists and has vibe check
          const response = await axios.get(`${API}/profiles/me`);
          
          if (!response.data.profile.vibe_check) {
            navigate('/vibe-check');
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Auth error:', error);
          toast.error('Authentication failed - please try again');
          navigate('/');
        } finally {
          setProcessing(false);
        }
      }
    };
    
    processAuth();
  }, [navigate, checkAuth]);
  
  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Processing authentication...</p>
        </div>
      </div>
    );
  }
  
  return null;
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" />;
  }
  
  return children;
}

// Layout
function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-brand-navy border-b border-slate-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/logos/badge-logo.png" alt="Asymbly Logo" className="h-10 w-auto" />
              <span className="text-2xl font-bold text-white">Asymbly</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="nav-home" className="text-white hover:text-brand-teal hover:bg-white/10">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
                <Button variant="ghost" onClick={() => navigate('/discover')} data-testid="nav-discover" className="text-white hover:text-brand-teal hover:bg-white/10">
                  <Search className="w-4 h-4 mr-2" />
                  Discover
                </Button>
                <Button variant="ghost" onClick={() => navigate('/campaigns')} data-testid="nav-campaigns" className="text-white hover:text-brand-teal hover:bg-white/10">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Campaigns
                </Button>
                <Button variant="ghost" onClick={() => navigate('/my-proposals')} data-testid="nav-proposals" className="text-white hover:text-brand-teal hover:bg-white/10">
                  📋
                  <span className="ml-2">Proposals</span>
                </Button>
                <Button variant="ghost" onClick={() => navigate('/connections')} data-testid="nav-connections" className="text-white hover:text-brand-teal hover:bg-white/10">
                  <Users className="w-4 h-4 mr-2" />
                  Connections
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate('/profile')} data-testid="nav-profile" className="text-white hover:text-brand-teal hover:bg-white/10">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
                <Button variant="outline" onClick={logout} data-testid="logout-button" className="text-white border-white hover:bg-brand-teal hover:border-brand-teal hover:text-white">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
            
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              className="lg:hidden text-white hover:text-brand-teal hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2 bg-white/10 rounded-lg p-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                onClick={() => {
                  navigate('/discover');
                  setMobileMenuOpen(false);
                }}
              >
                <Search className="w-4 h-4 mr-2" />
                Discover
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                onClick={() => {
                  navigate('/campaigns');
                  setMobileMenuOpen(false);
                }}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Campaigns
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                onClick={() => {
                  navigate('/my-proposals');
                  setMobileMenuOpen(false);
                }}
              >
                📋
                <span className="ml-2">My Proposals</span>
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                onClick={() => {
                  navigate('/connections');
                  setMobileMenuOpen(false);
                }}
              >
                <Users className="w-4 h-4 mr-2" />
                Connections
              </Button>
              <div className="border-t border-white/20 my-2 pt-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-white hover:text-brand-teal hover:bg-white/10" 
                  onClick={() => {
                    navigate('/profile');
                    setMobileMenuOpen(false);
                  }}
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start mt-2 text-white border-white hover:bg-brand-teal hover:border-brand-teal" 
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>
      
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
}

// ============ Forgot Password Page ============
function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [devCode, setDevCode] = useState(''); // For development display
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email
      });
      
      // Store code for development display
      setDevCode(response.data.code || '');
      
      toast.success('Password reset code sent to your email!');
      setSuccess(true);
      
      // Navigate to reset page after a short delay
      setTimeout(() => {
        navigate('/reset-password', { state: { email } });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-4 justify-center">
            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Asymbly</h1>
          </div>
          <CardTitle className="text-center">Forgot Password</CardTitle>
          <CardDescription className="text-center">
            Enter your email address and we'll send you a code to reset your password
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
              
              <div className="text-center text-sm text-slate-600 mt-4">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-slate-800 font-medium hover:underline"
                >
                  Sign In
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {devCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs text-amber-800 font-medium mb-2">🔧 DEVELOPMENT MODE</p>
                  <p className="text-sm text-amber-900 mb-1">Your reset code:</p>
                  <p className="text-3xl font-mono font-bold text-amber-900 tracking-widest text-center">
                    {devCode}
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    (This display is for testing only - will be removed in production)
                  </p>
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  Password reset code sent! Check your email and enter the code on the next page.
                </p>
              </div>
              
              <Button onClick={() => navigate('/reset-password', { state: { email } })} className="w-full">
                Continue to Reset Password
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Reset Password Page ============
function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      toast.error('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });
      
      toast.success('Password reset successful! Please log in with your new password.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Password reset failed');
      toast.error(err.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-4 justify-center">
            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Asymbly</h1>
          </div>
          <CardTitle className="text-center">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter the code from your email and choose a new password
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="code">Reset Code</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
              />
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            
            <div className="text-center text-sm text-slate-600 mt-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-slate-800 font-medium hover:underline"
              >
                Resend Code
              </button>
              {' '} | {' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-slate-800 font-medium hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Admin Cleanup Page ============
function AdminCleanupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  
  // Check if user is admin
  const isAdmin = user?.email === 'jerica.richardson@gmail.com' || user?.email === 'jerica.richardson@d2cobb.com';
  
  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle>Access Denied</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">You do not have permission to access this page.</p>
              <Button className="mt-4" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }
  
  const handleCleanup = async () => {
    if (confirmText !== 'DELETE TEST DATA') {
      toast.error('Please type the confirmation text correctly');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-test-data`, {
        cleanup_key: 'asymbly-cleanup-2024'
      });
      
      setResult(response.data);
      toast.success('Database cleanup completed!');
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteUser = async () => {
    if (!deleteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the user with email: ${deleteEmail}?`)) {
      return;
    }
    
    setDeleteLoading(true);
    try {
      const response = await axios.post(`${API}/admin/delete-test-user`, {
        email: deleteEmail
      });
      
      setDeleteResult(response.data);
      toast.success(`User deleted: ${response.data.message}`);
      setDeleteEmail('');
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-red-600">⚠️ Admin: Database Cleanup</CardTitle>
            <CardDescription>
              Remove all test data from the production database. This action cannot be undone.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-900 mb-2">What will be kept:</h3>
              <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                <li>Scout Smith</li>
                <li>Tolulope Olasanoye (Kevin)</li>
                <li>Kipley Carr (Kip)</li>
                <li>Jerica Richardson</li>
              </ul>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">What will be deleted:</h3>
              <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
                <li>All other test users</li>
                <li>Test campaigns and opportunities</li>
                <li>Test connections and proposals</li>
                <li>Test posts and ratings</li>
                <li>All MFA and password reset codes</li>
              </ul>
            </div>
            
            {!result ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="confirm">Type "DELETE TEST DATA" to confirm:</Label>
                  <Input
                    id="confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE TEST DATA"
                    className="mt-2"
                  />
                </div>
                
                <Button
                  variant="destructive"
                  onClick={handleCleanup}
                  disabled={loading || confirmText !== 'DELETE TEST DATA'}
                  className="w-full"
                >
                  {loading ? 'Cleaning...' : 'Clean Production Database'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">✅ Cleanup Completed!</h3>
                  <div className="text-sm text-green-800 space-y-2">
                    <p><strong>Real Users Kept:</strong> {result.real_users_kept?.length || 0}</p>
                    {result.real_users_kept?.map((user, idx) => (
                      <p key={idx} className="ml-4">• {user.name} ({user.email})</p>
                    ))}
                  </div>
                </div>
                
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-2">Deletion Summary:</h3>
                  <div className="text-sm text-slate-700 space-y-1">
                    {result.deleted_counts && Object.entries(result.deleted_counts).map(([key, value]) => (
                      <p key={key}>• {key}: {value} deleted</p>
                    ))}
                  </div>
                </div>
                
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  Go to Dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-xl">Delete Individual Test User</CardTitle>
            <CardDescription>
              Remove a specific test user by email address
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Use this to delete individual test users like "testuser1@firebrandspark.test" (Alex Smith)
              </p>
            </div>
            
            {deleteResult ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2">✅ User Deleted</h3>
                <p className="text-sm text-green-800">{deleteResult.message}</p>
                <Button 
                  onClick={() => setDeleteResult(null)} 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                >
                  Delete Another User
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="deleteEmail">Test User Email:</Label>
                  <Input
                    id="deleteEmail"
                    type="email"
                    value={deleteEmail}
                    onChange={(e) => setDeleteEmail(e.target.value)}
                    placeholder="testuser1@firebrandspark.test"
                    className="mt-2"
                  />
                </div>
                
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  disabled={deleteLoading || !deleteEmail}
                  className="w-full"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete This User'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

// ============ Vibe Check Page ============
function VibeCheckPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    // Demographics
    age: '', state: '', gender: '', education: '',
    // Liberty
    us_standing: '', fiscal_policy: 3, social_policy: 3,
    govt_economic_role: '', govt_religious_values: '',
    // Opportunity
    tech_optimism: 3, economic_growth_importance: '',
    poverty_solution: '', assess_candidates: '',
    // Value
    important_value: '', equal_rights_work: '',
    people_offended_problem: '', education_access_importance: 3,
    // Empathy
    core_values: '', political_discussions_freq: '',
    democrats_feeling: 50, republicans_feeling: 50,
    political_info_source: '',
    // Preferences
    employment_type: [], compensation: [], compensation_range: 0, responsibility_level: [],
    campaign_level: [], geographic_flexibility: [], political_ideology: [],
    work_location: [], hours_availability: [], campaign_size: [], work_culture: [], work_regions: []
  });
  
  const sections = [
    { title: 'Demographics', subtitle: 'Basic information about you' },
    { title: 'Liberty & Republic', subtitle: 'Your views on government and freedom' },
    { title: 'Opportunity & Future', subtitle: 'Your outlook on innovation and growth' },
    { title: 'Value & Community', subtitle: 'Your beliefs about equality and society' },
    { title: 'Empathy & Connection', subtitle: 'Your political engagement and values' },
    { title: 'Work Preferences', subtitle: 'Your professional preferences and goals' }
  ];
  
  useEffect(() => {
    checkExistingVibeCheck();
  }, []);
  
  const checkExistingVibeCheck = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`);
      if (response.data.profile.vibe_check) {
        // Load existing data for editing
        const vibeCheck = response.data.profile.vibe_check;
        const preferences = response.data.profile.preferences || {};
        setFormData({
          ...vibeCheck,
          ...preferences
        });
        setEditing(true);
      }
    } catch (error) {
      console.error('Error loading vibe check:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    try {
      const { employment_type, compensation, compensation_range, responsibility_level, campaign_level,
        geographic_flexibility, political_ideology, work_location, hours_availability,
        campaign_size, work_culture, work_regions, ...vibeCheck } = formData;
      
      const preferences = {
        employment_type, compensation, compensation_range, responsibility_level, campaign_level,
        geographic_flexibility, political_ideology, work_location, hours_availability,
        campaign_size, work_culture, work_regions
      };
      
      await axios.post(`${API}/profiles/vibe-check`, {
        vibe_check: vibeCheck,
        preferences
      });
      
      toast.success(editing ? 'Vibe Check updated!' : 'Vibe Check completed!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit Vibe Check');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Card data-testid="vibe-check-form">
          <CardHeader>
            <CardTitle className="text-3xl">
              {editing ? 'Edit Your Vibe Check' : 'Vibe Check Questionnaire'}
            </CardTitle>
            <CardDescription>
              {sections[step].subtitle} - Step {step + 1} of {sections.length}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="flex gap-2 mb-4">
                {sections.map((_, idx) => (
                  <div key={idx} className={`h-2 flex-1 rounded ${idx <= step ? 'bg-slate-700' : 'bg-slate-200'}`} />
                ))}
              </div>
              <h3 className="text-xl font-semibold mb-6">{sections[step].title}</h3>
              
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <Label>Age Group</Label>
                    <Select value={formData.age} onValueChange={(v) => setFormData({...formData, age: v})}>
                      <SelectTrigger><SelectValue placeholder="Select age" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="18-24">18-24</SelectItem>
                        <SelectItem value="25-34">25-34</SelectItem>
                        <SelectItem value="35-44">35-44</SelectItem>
                        <SelectItem value="45-54">45-54</SelectItem>
                        <SelectItem value="55-64">55-64</SelectItem>
                        <SelectItem value="65+">65+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})}>
                      <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value="AL">Alabama</SelectItem>
                        <SelectItem value="AK">Alaska</SelectItem>
                        <SelectItem value="AZ">Arizona</SelectItem>
                        <SelectItem value="AR">Arkansas</SelectItem>
                        <SelectItem value="CA">California</SelectItem>
                        <SelectItem value="CO">Colorado</SelectItem>
                        <SelectItem value="CT">Connecticut</SelectItem>
                        <SelectItem value="DE">Delaware</SelectItem>
                        <SelectItem value="FL">Florida</SelectItem>
                        <SelectItem value="GA">Georgia</SelectItem>
                        <SelectItem value="HI">Hawaii</SelectItem>
                        <SelectItem value="ID">Idaho</SelectItem>
                        <SelectItem value="IL">Illinois</SelectItem>
                        <SelectItem value="IN">Indiana</SelectItem>
                        <SelectItem value="IA">Iowa</SelectItem>
                        <SelectItem value="KS">Kansas</SelectItem>
                        <SelectItem value="KY">Kentucky</SelectItem>
                        <SelectItem value="LA">Louisiana</SelectItem>
                        <SelectItem value="ME">Maine</SelectItem>
                        <SelectItem value="MD">Maryland</SelectItem>
                        <SelectItem value="MA">Massachusetts</SelectItem>
                        <SelectItem value="MI">Michigan</SelectItem>
                        <SelectItem value="MN">Minnesota</SelectItem>
                        <SelectItem value="MS">Mississippi</SelectItem>
                        <SelectItem value="MO">Missouri</SelectItem>
                        <SelectItem value="MT">Montana</SelectItem>
                        <SelectItem value="NE">Nebraska</SelectItem>
                        <SelectItem value="NV">Nevada</SelectItem>
                        <SelectItem value="NH">New Hampshire</SelectItem>
                        <SelectItem value="NJ">New Jersey</SelectItem>
                        <SelectItem value="NM">New Mexico</SelectItem>
                        <SelectItem value="NY">New York</SelectItem>
                        <SelectItem value="NC">North Carolina</SelectItem>
                        <SelectItem value="ND">North Dakota</SelectItem>
                        <SelectItem value="OH">Ohio</SelectItem>
                        <SelectItem value="OK">Oklahoma</SelectItem>
                        <SelectItem value="OR">Oregon</SelectItem>
                        <SelectItem value="PA">Pennsylvania</SelectItem>
                        <SelectItem value="RI">Rhode Island</SelectItem>
                        <SelectItem value="SC">South Carolina</SelectItem>
                        <SelectItem value="SD">South Dakota</SelectItem>
                        <SelectItem value="TN">Tennessee</SelectItem>
                        <SelectItem value="TX">Texas</SelectItem>
                        <SelectItem value="UT">Utah</SelectItem>
                        <SelectItem value="VT">Vermont</SelectItem>
                        <SelectItem value="VA">Virginia</SelectItem>
                        <SelectItem value="WA">Washington</SelectItem>
                        <SelectItem value="WV">West Virginia</SelectItem>
                        <SelectItem value="WI">Wisconsin</SelectItem>
                        <SelectItem value="WY">Wyoming</SelectItem>
                        <SelectItem value="DC">Washington D.C.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non-binary">Non-binary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Education</Label>
                    <Select value={formData.education} onValueChange={(v) => setFormData({...formData, education: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high-school">High school</SelectItem>
                        <SelectItem value="some-college">Some college</SelectItem>
                        <SelectItem value="bachelors">Bachelor's</SelectItem>
                        <SelectItem value="masters">Master's+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <Label>Which statement comes closer to your view?</Label>
                    <Select value={formData.us_standing} onValueChange={(v) => setFormData({...formData, us_standing: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select your view" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="above-all">The U.S. stands above all other countries in the world</SelectItem>
                        <SelectItem value="one-of-greatest">The U.S. is one of the greatest countries, along with some others</SelectItem>
                        <SelectItem value="others-better">There are other countries that are better than the U.S.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>How would you describe your beliefs on fiscal policy?</Label>
                    <div className="mt-3 space-y-2">
                      {[
                        {value: 1, label: 'Very Conservative'},
                        {value: 2, label: 'Conservative'},
                        {value: 3, label: 'Moderate'},
                        {value: 4, label: 'Liberal'},
                        {value: 5, label: 'Very Liberal'}
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="fiscal_policy"
                            checked={formData.fiscal_policy === opt.value}
                            onChange={() => setFormData({...formData, fiscal_policy: opt.value})}
                            className="w-4 h-4"
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>How would you describe your beliefs on social policy?</Label>
                    <div className="mt-3 space-y-2">
                      {[
                        {value: 1, label: 'Very Conservative'},
                        {value: 2, label: 'Conservative'},
                        {value: 3, label: 'Moderate'},
                        {value: 4, label: 'Liberal'},
                        {value: 5, label: 'Very Liberal'}
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="social_policy"
                            checked={formData.social_policy === opt.value}
                            onChange={() => setFormData({...formData, social_policy: opt.value})}
                            className="w-4 h-4"
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Do you believe that government should play a significant role in regulating the economic forces that shape the market?</Label>
                    <Select value={formData.govt_economic_role} onValueChange={(v) => setFormData({...formData, govt_economic_role: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="significant">A very significant role</SelectItem>
                        <SelectItem value="some">Some kind of role</SelectItem>
                        <SelectItem value="little">Little or no role</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Do you believe that government policies should support religious values and beliefs?</Label>
                    <Select value={formData.govt_religious_values} onValueChange={(v) => setFormData({...formData, govt_religious_values: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes, religion should be a foundation of policy</SelectItem>
                        <SelectItem value="no">No, religion should be kept separate from government policies</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <Label>How optimistic are you about the ability of technology and AI to create new jobs and economic growth?</Label>
                    <div className="mt-3 space-y-2">
                      {[
                        {value: 1, label: 'Very Pessimistic'},
                        {value: 2, label: 'Pessimistic'},
                        {value: 3, label: 'Neutral'},
                        {value: 4, label: 'Optimistic'},
                        {value: 5, label: 'Very Optimistic'}
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="tech_optimism"
                            checked={formData.tech_optimism === opt.value}
                            onChange={() => setFormData({...formData, tech_optimism: opt.value})}
                            className="w-4 h-4"
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>How important is economic growth as a solution to poverty in America?</Label>
                    <Select value={formData.economic_growth_importance} onValueChange={(v) => setFormData({...formData, economic_growth_importance: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="most-important">The most important goal</SelectItem>
                        <SelectItem value="one-among-several">One among several goals</SelectItem>
                        <SelectItem value="not-critical">Not a critical goal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>In your opinion, what is the best solution to poverty in America?</Label>
                    <Select value={formData.poverty_solution} onValueChange={(v) => setFormData({...formData, poverty_solution: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free-markets">Economic growth and free markets</SelectItem>
                        <SelectItem value="government">Government intervention and social programs</SelectItem>
                        <SelectItem value="both">Both economic growth and government intervention</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>How do you assess political candidates?</Label>
                    <Textarea 
                      value={formData.assess_candidates} 
                      onChange={(e) => setFormData({...formData, assess_candidates: e.target.value})} 
                      rows={4}
                      placeholder="Share what factors are most important to you when evaluating candidates..."
                      className="mt-2"
                    />
                  </div>
                </div>
              )}
              
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <Label>Which statement comes closer to your view?</Label>
                    <Select value={formData.important_value} onValueChange={(v) => setFormData({...formData, important_value: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equal-opportunity">The most important value is equal opportunity</SelectItem>
                        <SelectItem value="economic-equality">The most important value is economic equality</SelectItem>
                        <SelectItem value="balanced">Should be balanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>How much more needs to be done to ensure equal rights for all Americans regardless of their racial or ethnic backgrounds?</Label>
                    <Select value={formData.equal_rights_work} onValueChange={(v) => setFormData({...formData, equal_rights_work: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a-lot">A lot</SelectItem>
                        <SelectItem value="a-little">A little</SelectItem>
                        <SelectItem value="nothing">Nothing at all</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>How much of a problem, if any, do you see in the country today with people being too easily offended by things others say?</Label>
                    <Select value={formData.people_offended_problem} onValueChange={(v) => setFormData({...formData, people_offended_problem: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="major">Major problem</SelectItem>
                        <SelectItem value="minor">Minor problem</SelectItem>
                        <SelectItem value="not-problem">Not a problem</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>How important is it for politicians to ensure everyone has access to quality education, regardless of their background?</Label>
                    <div className="mt-3 space-y-2">
                      {[
                        {value: 1, label: 'Not Important'},
                        {value: 2, label: 'Slightly Important'},
                        {value: 3, label: 'Moderately Important'},
                        {value: 4, label: 'Important'},
                        {value: 5, label: 'Very Important'}
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="education_access_importance"
                            checked={formData.education_access_importance === opt.value}
                            onChange={() => setFormData({...formData, education_access_importance: opt.value})}
                            className="w-4 h-4"
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {step === 4 && (
                <div className="space-y-6">
                  <div>
                    <Label>How do you define your core political values?</Label>
                    <Textarea 
                      value={formData.core_values} 
                      onChange={(e) => setFormData({...formData, core_values: e.target.value})} 
                      rows={4}
                      placeholder="Describe the core values that drive your political beliefs..."
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label>How often do you participate in political discussions with others?</Label>
                    <Select value={formData.political_discussions_freq} onValueChange={(v) => setFormData({...formData, political_discussions_freq: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="every-day">Every day</SelectItem>
                        <SelectItem value="weekly">At least once a week</SelectItem>
                        <SelectItem value="monthly">At least once a month</SelectItem>
                        <SelectItem value="less">Less than once a month</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>On a scale of 0 to 100, how do you feel toward Democrats?</Label>
                    <div className="mt-3">
                      <Slider value={[formData.democrats_feeling]} onValueChange={(v) => setFormData({...formData, democrats_feeling: v[0]})} min={0} max={100} step={5} />
                      <div className="flex justify-between text-xs text-slate-600 mt-2">
                        <span>Cold/Unfavorable</span>
                        <span className="font-semibold text-base">{formData.democrats_feeling}</span>
                        <span>Warm/Favorable</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>On a scale of 0 to 100, how do you feel toward Republicans?</Label>
                    <div className="mt-3">
                      <Slider value={[formData.republicans_feeling]} onValueChange={(v) => setFormData({...formData, republicans_feeling: v[0]})} min={0} max={100} step={5} />
                      <div className="flex justify-between text-xs text-slate-600 mt-2">
                        <span>Cold/Unfavorable</span>
                        <span className="font-semibold text-base">{formData.republicans_feeling}</span>
                        <span>Warm/Favorable</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>How do you prefer to receive political information?</Label>
                    <Select value={formData.political_info_source} onValueChange={(v) => setFormData({...formData, political_info_source: v})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="news-orgs">From news organizations for complete stories</SelectItem>
                        <SelectItem value="social-media">Directly from candidates on social media</SelectItem>
                        <SelectItem value="friends-family">From friends and family</SelectItem>
                        <SelectItem value="media-personalities">From political media personalities</SelectItem>
                        <SelectItem value="professional-workforce">From professional workforce</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {step === 5 && (
                <div className="space-y-6">
                  <div>
                    <Label>Employment Type (select all that apply)</Label>
                    <div className="space-y-2 mt-2">
                      {['Full-time', 'Part-time', 'Volunteering'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.employment_type.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, employment_type: e.target.checked ? [...formData.employment_type, opt] : formData.employment_type.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Compensation Preferences (select all that apply)</Label>
                    <div className="space-y-2 mt-2">
                      {['Paid work', 'Unpaid work', 'Limited funding okay'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.compensation.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, compensation: e.target.checked ? [...formData.compensation, opt] : formData.compensation.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Expected Monthly Compensation (USD)</Label>
                    <div className="mt-2 space-y-3">
                      <Slider
                        value={[formData.compensation_range || 0]}
                        onValueChange={(value) => setFormData({...formData, compensation_range: value[0]})}
                        max={10000}
                        step={500}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>$0 (Volunteer)</span>
                        <span className="font-semibold text-slate-800">
                          ${(formData.compensation_range || 0).toLocaleString()}/month
                        </span>
                        <span>$10,000+</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Level of Responsibility</Label>
                    <div className="space-y-2 mt-2">
                      {['Entry-level', 'Mid-level', 'Senior-level'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.responsibility_level.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, responsibility_level: e.target.checked ? [...formData.responsibility_level, opt] : formData.responsibility_level.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Campaign Level (select all that apply)</Label>
                    <div className="space-y-2 mt-2">
                      {['Local', 'State', 'Federal', 'Ballot initiatives'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.campaign_level.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, campaign_level: e.target.checked ? [...formData.campaign_level, opt] : formData.campaign_level.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Geographic Flexibility</Label>
                    <div className="space-y-2 mt-2">
                      {['Stay close to home', 'Willing to relocate', 'Willing to travel frequently'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.geographic_flexibility.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, geographic_flexibility: e.target.checked ? [...formData.geographic_flexibility, opt] : formData.geographic_flexibility.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Political Ideology of Campaign</Label>
                    <Select value={formData.political_ideology[0] || ''} onValueChange={(v) => setFormData({...formData, political_ideology: [v]})}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="democrat">Democrat</SelectItem>
                        <SelectItem value="republican">Republican</SelectItem>
                        <SelectItem value="independent">Independent/Other</SelectItem>
                        <SelectItem value="no-preference">No preference</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Work Location Preference</Label>
                    <div className="space-y-2 mt-2">
                      {['Office', 'Work from home', 'Mobile/Field work'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.work_location.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, work_location: e.target.checked ? [...formData.work_location, opt] : formData.work_location.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Work Culture Preference</Label>
                    <div className="space-y-2 mt-2">
                      {['High-pressure, competitive', 'Less competitive', 'Flexible hours', 'Intense hours near elections'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.work_culture.includes(opt)} 
                            onChange={(e) => {
                              setFormData({...formData, work_culture: e.target.checked ? [...formData.work_culture, opt] : formData.work_culture.filter(v => v !== opt)});
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Preferred Work Regions (select all that apply)</Label>
                    <div className="space-y-2 mt-2">
                      {[
                        'Northeast', 
                        'Southeast', 
                        'Midwest', 
                        'Southwest', 
                        'West', 
                        'Pacific Northwest', 
                        'National/Any Region'
                      ].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.work_regions?.includes(opt) || false}
                            onChange={(e) => {
                              const currentRegions = formData.work_regions || [];
                              setFormData({
                                ...formData, 
                                work_regions: e.target.checked 
                                  ? [...currentRegions, opt] 
                                  : currentRegions.filter(v => v !== opt)
                              });
                            }}
                            className="rounded"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
                Previous
              </Button>
              {step < sections.length - 1 ? (
                <Button onClick={() => setStep(step + 1)}>Next</Button>
              ) : (
                <Button onClick={handleSubmit}>Complete</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Dashboard, Profile, Discover, Campaigns, Connections (simplified for space)
function Dashboard() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/posts/feed`).then(r => setPosts(r.data.posts || [])).catch(e => console.error(e));
  }, []);
  
  const createPost = async () => {
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API}/posts`, { content: newPost });
      setNewPost('');
      toast.success('Post created!');
      axios.get(`${API}/posts/feed`).then(r => setPosts(r.data.posts || []));
    } catch (e) {
      toast.error('Failed');
    }
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Welcome, {user?.name}!</h1>
        <Card className="mb-8">
          <CardHeader><CardTitle>Share an Update</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={newPost} onChange={(e) => setNewPost(e.target.value)} rows={3} className="mb-4" />
            <Button onClick={createPost}><Plus className="w-4 h-4 mr-2" />Post</Button>
          </CardContent>
        </Card>
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">News Feed</h2>
          {posts.map(post => (
            <Card key={post.id}>
              <CardHeader>
                <CardTitle>{post.user?.name}</CardTitle>
              </CardHeader>
              <CardContent><p>{post.content}</p></CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPicture, setEditingPicture] = useState(false);
  const [pictureUrl, setPictureUrl] = useState('');
  const [formData, setFormData] = useState({
    headline: '',
    bio: '',
    location: '',
    linkedin_url: '',
    skills: [],
    experience: [],
    education: [],
    storytelling: '',
    profile_visibility: 'public'
  });
  
  const [editingExperience, setEditingExperience] = useState(false);
  const [experienceForm, setExperienceForm] = useState({
    title: '', organization: '', location: '', start_date: '', end_date: '', current: false, description: ''
  });
  
  const [editingEducation, setEditingEducation] = useState(false);
  const [educationForm, setEducationForm] = useState({
    school: '', degree: '', field: '', start_year: '', end_year: '', description: ''
  });
  
  useEffect(() => {
    loadProfile();
  }, []);
  
  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`);
      setProfile(response.data.profile);
      setFormData({
        headline: response.data.profile.headline || '',
        bio: response.data.profile.bio || '',
        location: response.data.profile.location || '',
        linkedin_url: response.data.profile.linkedin_url || '',
        skills: response.data.profile.skills || [],
        experience: response.data.profile.experience || [],
        education: response.data.profile.education || [],
        storytelling: response.data.profile.storytelling || '',
        profile_visibility: response.data.profile.profile_visibility || 'public'
      });
    } catch (error) {
      console.error('Profile load error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const saveProfile = async () => {
    try {
      await axios.put(`${API}/profiles/me`, formData);
      toast.success('Profile updated!');
      setEditing(false);
      loadProfile();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update profile');
    }
  };
  
  const updatePicture = async () => {
    if (!pictureUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    try {
      await axios.put(`${API}/users/me/picture`, { picture: pictureUrl });
      toast.success('Profile picture updated!');
      setEditingPicture(false);
      setPictureUrl('');
      window.location.reload(); // Reload to update auth context
    } catch (error) {
      console.error('Picture update error:', error);
      toast.error('Failed to update picture');
    }
  };
  
  const addExperience = () => {
    if (!experienceForm.title || !experienceForm.organization) {
      toast.error('Please fill in title and organization');
      return;
    }
    setFormData({
      ...formData,
      experience: [...formData.experience, { ...experienceForm, id: Date.now().toString() }]
    });
    setExperienceForm({ title: '', organization: '', location: '', start_date: '', end_date: '', current: false, description: '' });
    setEditingExperience(false);
  };
  
  const removeExperience = (id) => {
    setFormData({
      ...formData,
      experience: formData.experience.filter(exp => exp.id !== id)
    });
  };
  
  const addEducation = () => {
    if (!educationForm.school || !educationForm.degree) {
      toast.error('Please fill in school and degree');
      return;
    }
    setFormData({
      ...formData,
      education: [...formData.education, { ...educationForm, id: Date.now().toString() }]
    });
    setEducationForm({ school: '', degree: '', field: '', start_year: '', end_year: '', description: '' });
    setEditingEducation(false);
  };
  
  const removeEducation = (id) => {
    setFormData({
      ...formData,
      education: formData.education.filter(edu => edu.id !== id)
    });
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }
  
  if (!profile) {
    return (
      <Layout>
        <div className="text-center py-8 text-slate-600">Profile not found</div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="profile-page">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <div className="bg-gradient-to-r from-slate-700 to-slate-600 h-32 rounded-t-xl"></div>
              <CardContent className="relative pt-0 pb-6">
                <div className="relative inline-block">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-32 h-32 rounded-full border-4 border-white -mt-16 mb-4"
                      data-testid="profile-picture"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-white -mt-16 mb-4 bg-slate-200 flex items-center justify-center">
                      <User className="w-16 h-16 text-slate-400" />
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute bottom-4 right-0 rounded-full w-8 h-8 p-0"
                    onClick={() => {
                      setPictureUrl(user?.picture || '');
                      setEditingPicture(true);
                    }}
                  >
                    ✏️
                  </Button>
                </div>
                
                <div>
                  <h1 className="text-3xl font-bold text-slate-800 mb-1" data-testid="profile-name">
                    {user?.name}
                  </h1>
                  {profile.headline ? (
                    <p className="text-lg text-slate-700 mb-2" data-testid="profile-headline">
                      {profile.headline}
                    </p>
                  ) : editing && (
                    <p className="text-slate-500 text-sm mb-2">Add a headline</p>
                  )}
                  {profile.location && (
                    <p className="text-slate-600 text-sm mb-3">
                      {profile.location}
                    </p>
                  )}
                  {profile.linkedin_url && (
                    <a 
                      href={profile.linkedin_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mb-3"
                    >
                      🔗 LinkedIn Profile
                    </a>
                  )}
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </CardContent>
            </Card>
            
            {/* About Section */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>About</CardTitle>
                  {!editing && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditing(true)}
                      data-testid="edit-about-button"
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="headline">Headline</Label>
                      <Input
                        id="headline"
                        value={formData.headline}
                        onChange={(e) => setFormData({...formData, headline: e.target.value})}
                        placeholder="Professional headline"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        placeholder="Tell us about yourself..."
                        rows={6}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="City, State"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="linkedin_url">LinkedIn Profile URL</Label>
                      <Input
                        id="linkedin_url"
                        value={formData.linkedin_url}
                        onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                        placeholder="https://linkedin.com/in/yourprofile"
                        className="mt-2"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={saveProfile}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditing(false); loadProfile(); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {profile.bio ? (
                      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
                    ) : (
                      <p className="text-slate-500 text-sm">Add information about yourself</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* Skills Section */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Skills</CardTitle>
                  {!editing && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-4">
                    <SkillsSelector
                      selectedSkills={formData.skills}
                      onSkillsChange={(skills) => setFormData({...formData, skills})}
                      placeholder="Add your skills..."
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveProfile}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditing(false); loadProfile(); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {profile.skills && profile.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="px-3 py-1">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">Add skills to showcase your expertise</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
                
            {/* Experience Section */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Experience</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingExperience(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {editingExperience && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Title *</Label>
                        <Input 
                          value={experienceForm.title}
                          onChange={(e) => setExperienceForm({...experienceForm, title: e.target.value})}
                          placeholder="e.g., Campaign Manager"
                        />
                      </div>
                      <div>
                        <Label>Organization *</Label>
                        <Input 
                          value={experienceForm.organization}
                          onChange={(e) => setExperienceForm({...experienceForm, organization: e.target.value})}
                          placeholder="e.g., Smith for Senate"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Location</Label>
                        <Input 
                          value={experienceForm.location}
                          onChange={(e) => setExperienceForm({...experienceForm, location: e.target.value})}
                          placeholder="City, State"
                        />
                      </div>
                      <div>
                        <Label>Start Date</Label>
                        <Input 
                          type="month"
                          value={experienceForm.start_date}
                          onChange={(e) => setExperienceForm({...experienceForm, start_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input 
                          type="month"
                          value={experienceForm.end_date}
                          onChange={(e) => setExperienceForm({...experienceForm, end_date: e.target.value})}
                          disabled={experienceForm.current}
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox"
                        checked={experienceForm.current}
                        onChange={(e) => setExperienceForm({...experienceForm, current: e.target.checked, end_date: ''})}
                        className="rounded"
                      />
                      <span className="text-sm">I currently work here</span>
                    </label>
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        value={experienceForm.description}
                        onChange={(e) => setExperienceForm({...experienceForm, description: e.target.value})}
                        placeholder="Describe your responsibilities..."
                        rows={4}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addExperience}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingExperience(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                
                {formData.experience && formData.experience.length > 0 ? (
                  <div className="space-y-6">
                    {formData.experience.map((exp) => (
                      <div key={exp.id} className="group">
                        <div className="flex gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <div>
                                <h4 className="text-lg font-semibold text-slate-800">{exp.title}</h4>
                                <p className="text-slate-700">{exp.organization}</p>
                                <p className="text-sm text-slate-500">
                                  {exp.start_date && new Date(exp.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                                  {' - '}
                                  {exp.current ? 'Present' : exp.end_date && new Date(exp.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                                  {exp.location && ` · ${exp.location}`}
                                </p>
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => removeExperience(exp.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            {exp.description && (
                              <p className="text-slate-600 mt-2 whitespace-pre-wrap">{exp.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Add your professional experience</p>
                )}
              </CardContent>
            </Card>
            
            {/* Education Section */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Education</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingEducation(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {editingEducation && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>School *</Label>
                        <Input 
                          value={educationForm.school}
                          onChange={(e) => setEducationForm({...educationForm, school: e.target.value})}
                          placeholder="e.g., UC Berkeley"
                        />
                      </div>
                      <div>
                        <Label>Degree *</Label>
                        <Input 
                          value={educationForm.degree}
                          onChange={(e) => setEducationForm({...educationForm, degree: e.target.value})}
                          placeholder="e.g., Bachelor of Arts"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Field of Study</Label>
                        <Input 
                          value={educationForm.field}
                          onChange={(e) => setEducationForm({...educationForm, field: e.target.value})}
                          placeholder="Political Science"
                        />
                      </div>
                      <div>
                        <Label>Start Year</Label>
                        <Input 
                          type="number"
                          value={educationForm.start_year}
                          onChange={(e) => setEducationForm({...educationForm, start_year: e.target.value})}
                          placeholder="2015"
                        />
                      </div>
                      <div>
                        <Label>End Year</Label>
                        <Input 
                          type="number"
                          value={educationForm.end_year}
                          onChange={(e) => setEducationForm({...educationForm, end_year: e.target.value})}
                          placeholder="2019"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        value={educationForm.description}
                        onChange={(e) => setEducationForm({...educationForm, description: e.target.value})}
                        placeholder="Activities, honors..."
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addEducation}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingEducation(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                
                {formData.education && formData.education.length > 0 ? (
                  <div className="space-y-4">
                    {formData.education.map((edu) => (
                      <div key={edu.id} className="group">
                        <div className="flex gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl">🎓</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <div>
                                <h4 className="font-semibold text-slate-800">{edu.school}</h4>
                                <p className="text-slate-700">
                                  {edu.degree}{edu.field && `, ${edu.field}`}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {edu.start_year && edu.end_year && `${edu.start_year} - ${edu.end_year}`}
                                </p>
                                {edu.description && (
                                  <p className="text-sm text-slate-600 mt-1">{edu.description}</p>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => removeEducation(edu.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Add your education</p>
                )}
              </CardContent>
            </Card>
            
            {/* Story Section */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>My Story</CardTitle>
                  {!editing && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-4">
                    <Textarea
                      value={formData.storytelling}
                      onChange={(e) => setFormData({...formData, storytelling: e.target.value})}
                      placeholder="Share your political journey..."
                      rows={8}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveProfile}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditing(false); loadProfile(); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {profile.storytelling ? (
                      <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{profile.storytelling}</p>
                    ) : (
                      <p className="text-slate-500 text-sm">Share your story</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Right Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Info Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Profile visibility</p>
                    <p className="text-sm text-slate-600">
                      {profile.profile_visibility === 'public' ? 'Public' :
                       profile.profile_visibility === 'connections' ? 'Connections only' :
                       'Private'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">User type</p>
                    <Badge variant="secondary">
                      {user?.user_type === 'individual' ? 'Individual' : 'Candidate'}
                    </Badge>
                  </div>
                  
                  {profile.vibe_check && (
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Vibe Check</p>
                      <Badge className="bg-green-100 text-green-800 border-green-200 mb-2">
                        ✓ Complete
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate('/vibe-check')}
                        className="w-full"
                        data-testid="edit-vibe-check-button"
                      >
                        Edit Answers
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Education Summary Card */}
            {formData.education && formData.education.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {formData.education.map((edu, idx) => (
                      <div key={idx} className="flex gap-2 items-start">
                        <span className="text-lg">🎓</span>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{edu.school}</p>
                          <p className="text-xs text-slate-600">{edu.degree}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        
        {/* Edit Picture Dialog */}
        {editingPicture && (
          <Dialog open={editingPicture} onOpenChange={setEditingPicture}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Update Profile Picture</DialogTitle>
                <DialogDescription>
                  Enter the URL of your profile picture
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="picture-url">Image URL</Label>
                  <Input
                    id="picture-url"
                    value={pictureUrl}
                    onChange={(e) => setPictureUrl(e.target.value)}
                    placeholder="https://example.com/your-picture.jpg"
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Paste the URL of an image hosted online
                  </p>
                </div>
                
                {pictureUrl && (
                  <div>
                    <Label>Preview</Label>
                    <div className="mt-2 flex justify-center">
                      <img 
                        src={pictureUrl} 
                        alt="Preview" 
                        className="w-32 h-32 rounded-full object-cover border-2 border-slate-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={updatePicture} className="flex-1">
                    Update Picture
                  </Button>
                  <Button variant="outline" onClick={() => {
                    setEditingPicture(false);
                    setPictureUrl('');
                  }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
                
      </div>
    </Layout>
  );
}

// ============ User Profile Page (View Others) ============
function UserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('none'); // none, pending_sent, pending_received, connected
  
  useEffect(() => {
    if (userId) {
      loadProfile();
      loadConnectionStatus();
    }
  }, [userId]);
  
  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/${userId}`);
      setProfile(response.data.profile);
      setUser(response.data.user);
    } catch (error) {
      console.error('Profile load error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const loadConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/connections/status/${userId}`);
      setConnectionStatus(response.data.status);
    } catch (error) {
      console.error('Connection status error:', error);
    }
  };
  
  const sendConnectionRequest = async () => {
    try {
      await axios.post(`${API}/connections/send?recipient_id=${userId}`);
      toast.success('Connection request sent!');
      setConnectionStatus('pending_sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };
  
  const getConnectionButton = () => {
    if (connectionStatus === 'connected') {
      return (
        <Button disabled className="mt-16 bg-slate-400 text-white hover:bg-slate-400">
          <Users className="w-4 h-4 mr-2" />
          Connected
        </Button>
      );
    } else if (connectionStatus === 'pending_sent') {
      return (
        <Button disabled className="mt-16 bg-slate-400 text-white hover:bg-slate-400">
          <Users className="w-4 h-4 mr-2" />
          Request Sent
        </Button>
      );
    } else if (connectionStatus === 'pending_received') {
      return (
        <Button disabled className="mt-16 bg-slate-400 text-white hover:bg-slate-400">
          <Users className="w-4 h-4 mr-2" />
          Request Pending
        </Button>
      );
    } else {
      return (
        <Button onClick={sendConnectionRequest} className="mt-16">
          <Users className="w-4 h-4 mr-2" />
          Connect
        </Button>
      );
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }
  
  if (!profile || !user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Profile Not Found</h2>
          <Button onClick={() => navigate('/discover')}>Back to Discover</Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          ← Back
        </Button>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <div className="bg-gradient-to-r from-slate-700 to-slate-600 h-32 rounded-t-xl"></div>
              <CardContent className="relative pt-0 pb-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-32 h-32 rounded-full border-4 border-white -mt-16 mb-4"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full border-4 border-white -mt-16 mb-4 bg-slate-200 flex items-center justify-center">
                        <User className="w-16 h-16 text-slate-400" />
                      </div>
                    )}
                    
                    <div>
                      <h1 className="text-3xl font-bold text-slate-800 mb-1">{user.name}</h1>
                      {profile.headline && (
                        <p className="text-lg text-slate-700 mb-2">{profile.headline}</p>
                      )}
                      {profile.location && (
                        <p className="text-slate-600 text-sm mb-3">📍 {profile.location}</p>
                      )}
                      {profile.linkedin_url && (
                        <a 
                          href={profile.linkedin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 mb-3"
                        >
                          🔗 LinkedIn Profile
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {getConnectionButton()}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Badge variant="secondary">
                    {user.user_type === 'individual' ? 'Individual' : 'Candidate'}
                  </Badge>
                  {profile.match_percentage && (
                    <Badge className={getMatchBadgeColor(profile.match_percentage)}>
                      {profile.match_percentage}% Match
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* About Section */}
            {profile.bio && (
              <Card>
                <CardHeader>
                  <CardTitle>About</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Skills Section */}
            {profile.skills && profile.skills.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Skills & Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Experience Section */}
            {profile.experience && profile.experience.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {profile.experience.map((exp, idx) => (
                      <div key={idx} className="border-b border-slate-200 last:border-0 pb-4 last:pb-0">
                        <h3 className="font-semibold text-slate-800">{exp.title}</h3>
                        <p className="text-slate-700">{exp.organization}</p>
                        {exp.location && <p className="text-sm text-slate-500">{exp.location}</p>}
                        <p className="text-sm text-slate-500 mt-1">
                          {exp.start_date} - {exp.current ? 'Present' : exp.end_date}
                        </p>
                        {exp.description && (
                          <p className="text-slate-600 mt-2 whitespace-pre-wrap">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Education Section */}
            {profile.education && profile.education.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {profile.education.map((edu, idx) => (
                      <div key={idx} className="border-b border-slate-200 last:border-0 pb-4 last:pb-0">
                        <h3 className="font-semibold text-slate-800">{edu.school}</h3>
                        <p className="text-slate-700">{edu.degree}{edu.field ? `, ${edu.field}` : ''}</p>
                        <p className="text-sm text-slate-500">
                          {edu.start_year} - {edu.end_year}
                        </p>
                        {edu.description && (
                          <p className="text-slate-600 mt-2">{edu.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Story Section */}
            {profile.storytelling && (
              <Card>
                <CardHeader>
                  <CardTitle>Their Story</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{profile.storytelling}</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Work Preferences Card */}
            {profile.preferences && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Work Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {profile.preferences.employment_type && profile.preferences.employment_type.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Employment Type</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.preferences.employment_type.map((type, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{type}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {profile.preferences.campaign_level && profile.preferences.campaign_level.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Campaign Level</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.preferences.campaign_level.map((level, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{level}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {profile.preferences.work_location && profile.preferences.work_location.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Work Location</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.preferences.work_location.map((loc, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{loc}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function DiscoverPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [filters, setFilters] = useState({
    name: '',
    location: '',
    skills: '',
    user_type: 'all',
    employment_type: 'all',
    campaign_level: 'all',
    min_match: '',
    max_match: ''
  });
  
  useEffect(() => {
    search();
  }, []);
  
  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.name) params.append('name', filters.name);
      if (filters.location) params.append('location', filters.location);
      if (filters.skills) params.append('skills', filters.skills);
      if (filters.user_type && filters.user_type !== 'all') params.append('user_type', filters.user_type);
      if (filters.min_match) params.append('min_match', filters.min_match);
      if (filters.max_match) params.append('max_match', filters.max_match);
      
      const response = await axios.get(`${API}/search?${params.toString()}`);
      const users = response.data.results || [];
      
      // Filter out current user from results
      const filteredUsers = users.filter(result => result.user?.id !== user?.id);
      setResults(filteredUsers);
      
      // Load connection statuses for all users
      const statuses = {};
      for (const result of filteredUsers) {
        const userId = result.user?.id;
        if (userId) {
          try {
            const statusResponse = await axios.get(`${API}/connections/status/${userId}`);
            statuses[userId] = statusResponse.data.status;
          } catch (error) {
            console.error('Status check error for user', userId, error);
            statuses[userId] = 'none';
          }
        }
      }
      setConnectionStatuses(statuses);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };
  
  const sendConnectionRequest = async (userId) => {
    try {
      await axios.post(`${API}/connections/send?recipient_id=${userId}`);
      toast.success('Connection request sent!');
      setConnectionStatuses({...connectionStatuses, [userId]: 'pending_sent'});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };
  
  const getConnectionButton = (userId) => {
    const status = connectionStatuses[userId] || 'none';
    
    if (status === 'connected') {
      return (
        <Button size="sm" disabled className="flex-1 bg-slate-400 text-white hover:bg-slate-400">
          Connected
        </Button>
      );
    } else if (status === 'pending_sent') {
      return (
        <Button size="sm" disabled className="flex-1 bg-slate-400 text-white hover:bg-slate-400">
          Request Sent
        </Button>
      );
    } else if (status === 'pending_received') {
      return (
        <Button size="sm" disabled className="flex-1 bg-slate-400 text-white hover:bg-slate-400">
          Request Pending
        </Button>
      );
    } else {
      return (
        <Button size="sm" onClick={() => sendConnectionRequest(userId)} className="flex-1">
          <Users className="w-3 h-3 mr-1" />
          Connect
        </Button>
      );
    }
  };
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="discover-page">
        <h1 className="text-3xl font-bold text-slate-800 mb-8">Discover Political Talent</h1>
        
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Search Filters</CardTitle>
            <CardDescription>Find political professionals that match your criteria</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={filters.name}
                    onChange={(e) => setFilters({...filters, name: e.target.value})}
                    placeholder="Search by name"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label>Location</Label>
                  <Input
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                    placeholder="e.g., California"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label>Skills</Label>
                  <Input
                    value={filters.skills}
                    onChange={(e) => setFilters({...filters, skills: e.target.value})}
                    placeholder="e.g., Fundraising"
                    className="mt-2"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label>User Type</Label>
                  <Select value={filters.user_type} onValueChange={(v) => setFilters({...filters, user_type: v})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="candidate">Candidate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Min Match %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.min_match}
                    onChange={(e) => setFilters({...filters, min_match: e.target.value})}
                    placeholder="0"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label>Max Match %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.max_match}
                    onChange={(e) => setFilters({...filters, max_match: e.target.value})}
                    placeholder="100"
                    className="mt-2"
                  />
                </div>
                
                <div className="flex items-end">
                  <Button onClick={search} className="w-full" data-testid="search-button">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : results.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Search className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Results Found</h3>
              <p className="text-slate-600">Try adjusting your filters or complete your Vibe Check for better matches!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {results.map(result => (
              <Card key={result.user.id} className="hover-card" data-testid={`search-result-${result.user.id}`}>
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <img 
                      src={result.user.picture || 'https://via.placeholder.com/80'} 
                      alt={result.user.name}
                      className="w-16 h-16 rounded-full"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <CardTitle className="text-lg">{result.user.name}</CardTitle>
                          {result.profile.headline && (
                            <p className="text-sm text-slate-500">{result.profile.headline}</p>
                          )}
                        </div>
                        {result.match_percentage && (
                          <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="match-percentage">
                            {result.match_percentage}% Match
                          </Badge>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {result.user.user_type}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {result.profile.location && (
                    <p className="text-sm text-slate-500 mb-3">📍 {result.profile.location}</p>
                  )}
                  
                  {result.profile.bio && (
                    <p className="text-slate-600 mb-4 line-clamp-2 text-sm">{result.profile.bio}</p>
                  )}
                  
                  {result.profile.skills && result.profile.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {result.profile.skills.slice(0, 3).map((skill, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                      ))}
                      {result.profile.skills.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{result.profile.skills.length - 3}</Badge>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/profile/${result.user.id}`)}
                      data-testid={`view-profile-${result.user.id}`}
                      className="flex-1"
                    >
                      View Profile
                    </Button>
                    {getConnectionButton(result.user.id)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    campaign_type: 'local',
    location: '',
    skills_needed: []
  });
  
  useEffect(() => {
    loadCampaigns();
  }, []);
  
  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Campaigns load error:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };
  
  const createCampaign = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    try {
      await axios.post(`${API}/campaigns`, formData);
      toast.success('Campaign created!');
      setShowCreate(false);
      setFormData({ title: '', description: '', campaign_type: 'local', location: '', skills_needed: [] });
      loadCampaigns();
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to create campaign');
    }
  };
  
  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="campaigns-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Campaigns & Projects</h1>
          <Button onClick={() => setShowCreate(!showCreate)} data-testid="create-campaign-button" className="bg-brand-navy hover:bg-slate-800 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </div>
        
        {showCreate && (
          <Card className="mb-8" data-testid="create-campaign-form">
            <CardHeader>
              <CardTitle>Create New Campaign</CardTitle>
              <CardDescription>Share your campaign or project with the Asymbly community</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Campaign Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., State Senate Campaign 2026"
                  className="mt-2"
                  data-testid="campaign-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe your campaign vision, goals, and what you're looking to accomplish..."
                  rows={4}
                  className="mt-2"
                  data-testid="campaign-description-input"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Campaign Type</Label>
                  <Select value={formData.campaign_type} onValueChange={(v) => setFormData({...formData, campaign_type: v})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">Local</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                      <SelectItem value="federal">Federal</SelectItem>
                      <SelectItem value="project">Project/Initiative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="e.g., California"
                    className="mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="skills">Skills Needed</Label>
                <div className="mt-2">
                  <SkillsSelector
                    selectedSkills={formData.skills_needed}
                    onSkillsChange={(skills) => setFormData({...formData, skills_needed: skills})}
                    placeholder="Start typing to search skills needed for this campaign..."
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={createCampaign} data-testid="submit-campaign-button">
                  Create Campaign
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Campaigns Yet</h3>
              <p className="text-slate-600 mb-4">Be the first to create a campaign or project!</p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {campaigns.map(campaign => (
              <Card key={campaign.id} className="hover-card" data-testid={`campaign-${campaign.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex gap-2">
                      <Badge variant="secondary">{campaign.campaign_type}</Badge>
                      {campaign.location && (
                        <Badge variant="outline">📍 {campaign.location}</Badge>
                      )}
                    </div>
                    {campaign.vibe_match_percentage !== undefined && (
                      <Badge variant="default" className={getMatchBadgeColor(campaign.vibe_match_percentage)}>
                        🎯 {campaign.vibe_match_percentage}% Match
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl">{campaign.title}</CardTitle>
                  <CardDescription>by {campaign.creator?.name || 'Unknown'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4 line-clamp-3">{campaign.description}</p>
                  
                  {campaign.open_positions_count > 0 && (
                    <div className="mb-4">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        💼 {campaign.open_positions_count} Open Position{campaign.open_positions_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  )}
                  
                  {campaign.skills_needed && campaign.skills_needed.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">Skills Needed:</p>
                      <div className="flex flex-wrap gap-2">
                        {campaign.skills_needed.slice(0, 4).map((skill, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                        ))}
                        {campaign.skills_needed.length > 4 && (
                          <Badge variant="outline" className="text-xs">+{campaign.skills_needed.length - 4} more</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    data-testid={`view-campaign-${campaign.id}`}
                    className="w-full"
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ============ Campaign Detail Page ============
function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalStep, setProposalStep] = useState(1); // 1 = review, 2 = submit
  const [myProfile, setMyProfile] = useState(null);
  const [myRatings, setMyRatings] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  
  // Edit campaign state
  const [showEditCampaign, setShowEditCampaign] = useState(false);
  const [editCampaignData, setEditCampaignData] = useState({
    title: '',
    description: '',
    campaign_type: '',
    skills_needed: [],
    location: '',
    primary_match_person_id: ''
  });
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Opportunities state
  const [opportunities, setOpportunities] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);
  
  const [proposalData, setProposalData] = useState({
    cover_message: '',
    relevant_experience: '',
    skills: [],
    location: '',
    compensation_expectation: 0,
    opportunity_id: null
  });
  
  useEffect(() => {
    loadCampaign();
    loadMyProfile();
  }, [id]);
  
  useEffect(() => {
    if (campaign && currentUser && campaign.creator?.id === currentUser.id) {
      loadProposals();
    }
  }, [campaign, currentUser]);
  
  const loadCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      console.error('Campaign load error:', error);
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };
  
  const loadMyProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`);
      setMyProfile(response.data.profile);
      
      // Load ratings
      if (currentUser) {
        const ratingsResponse = await axios.get(`${API}/ratings/${currentUser.id}`);
        setMyRatings(ratingsResponse.data);
      }
    } catch (error) {
      console.error('Profile load error:', error);
    }
  };
  
  const loadProposals = async () => {
    setLoadingProposals(true);
    try {
      const response = await axios.get(`${API}/campaigns/${id}/proposals`);
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Proposals load error:', error);
    } finally {
      setLoadingProposals(false);
    }
  };
  
  // Load opportunities
  useEffect(() => {
    if (campaign) {
      loadOpportunities();
    }
  }, [campaign]);
  
  const loadOpportunities = async () => {
    setLoadingOpportunities(true);
    try {
      const response = await axios.get(`${API}/campaigns/${id}/opportunities`);
      setOpportunities(response.data.opportunities || []);
    } catch (error) {
      console.error('Opportunities load error:', error);
    } finally {
      setLoadingOpportunities(false);
    }
  };
  
  const handleEditCampaign = async () => {
    setEditCampaignData({
      title: campaign.campaign?.title || '',
      description: campaign.campaign?.description || '',
      campaign_type: campaign.campaign?.campaign_type || '',
      skills_needed: campaign.campaign?.skills_needed || [],
      location: campaign.campaign?.location || '',
      primary_match_person_id: campaign.campaign?.primary_match_person_id || currentUser?.id || ''
    });
    
    // Load users for autocomplete
    try {
      const response = await axios.get(`${API}/search?location=&skills=`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Users load error:', error);
    }
    
    setShowEditCampaign(true);
  };
  
  const handleUpdateCampaign = async () => {
    try {
      await axios.put(`${API}/campaigns/${id}`, editCampaignData);
      toast.success('Campaign updated!');
      setShowEditCampaign(false);
      loadCampaign();
    } catch (error) {
      console.error('Update campaign error:', error);
      toast.error('Failed to update campaign');
    }
  };
  
  const handleExpressInterest = (opportunityId = null) => {
    if (!myProfile) {
      toast.error('Please complete your profile first');
      navigate('/profile');
      return;
    }
    
    // Pre-populate form with profile data
    setProposalData({
      cover_message: '',
      relevant_experience: '',
      skills: myProfile.skills || [],
      location: myProfile.location || '',
      compensation_expectation: myProfile.preferences?.compensation_range || 0,
      opportunity_id: opportunityId
    });
    
    setProposalStep(1);
    setShowProposalModal(true);
  };
  
  const handleSubmitProposal = async () => {
    if (!proposalData.cover_message.trim() || !proposalData.relevant_experience.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/campaigns/${id}/proposals`, {
        cover_message: proposalData.cover_message,
        relevant_experience: proposalData.relevant_experience,
        opportunity_id: proposalData.opportunity_id
      });
      
      toast.success('Proposal submitted successfully!');
      setShowProposalModal(false);
      setProposalStep(1);
      setProposalData({
        cover_message: '',
        relevant_experience: '',
        skills: [],
        location: '',
        compensation_expectation: 0,
        opportunity_id: null
      });
    } catch (error) {
      console.error('Proposal submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit proposal');
    }
  };
  
  const handleProposalStatus = async (proposalId, status) => {
    try {
      await axios.put(`${API}/proposals/${proposalId}/status?status=${status}`);
      toast.success(`Proposal ${status}!`);
      loadProposals(); // Reload proposals
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update proposal status');
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }
  
  if (!campaign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Campaign Not Found</h2>
          <Button onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
        </div>
      </Layout>
    );
  }
  
  const isOwner = currentUser && campaign.creator?.id === currentUser.id;
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/campaigns')} className="mb-6">
          ← Back to Campaigns
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div className="flex gap-2">
                <Badge variant="secondary">{campaign.campaign?.campaign_type}</Badge>
                {campaign.campaign?.location && (
                  <Badge variant="outline">📍 {campaign.campaign.location}</Badge>
                )}
                {campaign.vibe_match_percentage !== undefined && !isOwner && (
                  <Badge variant="default" className={getMatchBadgeColor(campaign.vibe_match_percentage)}>
                    🎯 {campaign.vibe_match_percentage}% Match
                  </Badge>
                )}
              </div>
              {isOwner && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleEditCampaign}>
                    Edit Campaign
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this campaign? This will also delete all associated opportunities and proposals.')) {
                        try {
                          await axios.delete(`${API}/campaigns/${id}`);
                          toast.success('Campaign deleted successfully');
                          navigate('/campaigns');
                        } catch (error) {
                          console.error('Delete error:', error);
                          toast.error('Failed to delete campaign');
                        }
                      }
                    }}
                  >
                    Delete Campaign
                  </Button>
                </div>
              )}
            </div>
            <CardTitle className="text-3xl mb-2">{campaign.campaign?.title}</CardTitle>
            {campaign.creator && (
              <CardDescription className="text-base">
                Created by {campaign.creator.name}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">About This Campaign</h3>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                {campaign.campaign?.description}
              </p>
            </div>
            
            {campaign.campaign?.skills_needed && campaign.campaign.skills_needed.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Skills Needed</h3>
                <div className="flex flex-wrap gap-2">
                  {campaign.campaign.skills_needed.map((skill, idx) => (
                    <Badge key={idx} variant="outline" className="px-3 py-1">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {campaign.creator && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Campaign Organizer</h3>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <img 
                    src={campaign.creator.picture || 'https://via.placeholder.com/64'}
                    alt={campaign.creator.name}
                    className="w-16 h-16 rounded-full"
                  />
                  <div>
                    <p className="font-semibold text-slate-800">{campaign.creator.name}</p>
                    <p className="text-sm text-slate-500">{campaign.creator.email}</p>
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {campaign.creator.user_type}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
            
            {!isOwner && (
              <div className="flex gap-2 pt-4">
                <Dialog open={showProposalModal} onOpenChange={setShowProposalModal}>
                  <DialogTrigger asChild>
                    <Button className="flex-1" onClick={handleExpressInterest}>
                      Express Interest
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {proposalStep === 1 ? 'Review Your Proposal' : 'Submit Proposal'}
                      </DialogTitle>
                      <DialogDescription>
                        {proposalStep === 1 
                          ? 'Review and edit your information before submitting' 
                          : 'Final step: Add your message and experience'}
                      </DialogDescription>
                    </DialogHeader>
                    
                    {proposalStep === 1 ? (
                      <div className="space-y-4">
                        <div>
                          <Label>Skills</Label>
                          <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 rounded-lg">
                            {proposalData.skills.map((skill, idx) => (
                              <Badge key={idx} variant="secondary">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="location">Location</Label>
                          <Input
                            id="location"
                            value={proposalData.location}
                            onChange={(e) => setProposalData({...proposalData, location: e.target.value})}
                            placeholder="Your location"
                            className="mt-2"
                          />
                        </div>
                        
                        {myProfile?.preferences && (
                          <div>
                            <Label>Work Preferences</Label>
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                              {myProfile.preferences.employment_type?.length > 0 && (
                                <p><strong>Employment:</strong> {myProfile.preferences.employment_type.join(', ')}</p>
                              )}
                              {myProfile.preferences.work_location?.length > 0 && (
                                <p><strong>Work Location:</strong> {myProfile.preferences.work_location.join(', ')}</p>
                              )}
                              {myProfile.preferences.campaign_level?.length > 0 && (
                                <p><strong>Campaign Level:</strong> {myProfile.preferences.campaign_level.join(', ')}</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <Label htmlFor="compensation">Compensation Expectation (Monthly USD)</Label>
                          <div className="mt-2">
                            <Input
                              type="number"
                              id="compensation"
                              value={proposalData.compensation_expectation}
                              onChange={(e) => setProposalData({...proposalData, compensation_expectation: parseInt(e.target.value) || 0})}
                              placeholder="0"
                              min="0"
                              max="10000"
                            />
                            <p className="text-sm text-slate-500 mt-1">
                              ${proposalData.compensation_expectation.toLocaleString()}/month
                            </p>
                          </div>
                        </div>
                        
                        {myRatings && myRatings.average > 0 && (
                          <div>
                            <Label>Your Rating</Label>
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                              <p className="text-2xl font-bold text-slate-800">
                                ⭐ {myRatings.average.toFixed(1)} / 5.0
                              </p>
                              <p className="text-sm text-slate-500">
                                Based on {myRatings.count} review{myRatings.count !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex gap-2 pt-4">
                          <Button onClick={() => setProposalStep(2)} className="flex-1">
                            Next: Add Message
                          </Button>
                          <Button variant="outline" onClick={() => setShowProposalModal(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="cover_message">Cover Message *</Label>
                          <Textarea
                            id="cover_message"
                            value={proposalData.cover_message}
                            onChange={(e) => setProposalData({...proposalData, cover_message: e.target.value})}
                            placeholder="Introduce yourself and explain why you're interested in this campaign..."
                            rows={4}
                            className="mt-2"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="relevant_experience">Relevant Experience *</Label>
                          <Textarea
                            id="relevant_experience"
                            value={proposalData.relevant_experience}
                            onChange={(e) => setProposalData({...proposalData, relevant_experience: e.target.value})}
                            placeholder="Describe your relevant experience and qualifications..."
                            rows={6}
                            className="mt-2"
                          />
                        </div>
                        
                        <div className="flex gap-2 pt-4">
                          <Button onClick={() => setProposalStep(1)} variant="outline">
                            ← Back
                          </Button>
                          <Button onClick={handleSubmitProposal} className="flex-1">
                            Submit Proposal
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                <Button variant="outline" className="flex-1">
                  Share Campaign
                </Button>
              </div>
            )}
            
            {/* Opportunities Section */}
            <div className="border-t border-slate-200 pt-6 mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-slate-800">
                  Opportunities {isOwner && `(${opportunities.length})`}
                </h3>
                {isOwner && (
                  <Button size="sm" onClick={() => navigate(`/campaigns/${id}/opportunities/create`)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Opportunity
                  </Button>
                )}
              </div>
              
              {loadingOpportunities ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : opportunities.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p>{isOwner ? 'No opportunities created yet' : 'No open opportunities available'}</p>
                  {isOwner && (
                    <Button className="mt-4" size="sm" onClick={() => navigate(`/campaigns/${id}/opportunities/create`)}>
                      Create First Opportunity
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {opportunities.map((opp) => (
                    <Card 
                      key={opp.id} 
                      className={`${opp.status === 'filled' && isOwner ? 'opacity-50' : ''} hover:shadow-md transition-shadow cursor-pointer`}
                      onClick={() => navigate(`/opportunities/${opp.id}`)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-lg font-semibold text-slate-800">{opp.job_title}</h4>
                              {opp.vibe_match_percentage !== undefined && !isOwner && (
                                <Badge variant="default" className={getMatchBadgeColor(opp.vibe_match_percentage)}>
                                  🎯 {opp.vibe_match_percentage}% Match
                                </Badge>
                              )}
                              {opp.status === 'filled' && (
                                <Badge variant="secondary">Filled</Badge>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm line-clamp-2">{opp.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {opp.compensation_negotiable ? (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-800 border-blue-200">
                              💰 Negotiable
                            </Badge>
                          ) : (opp.compensation_range_min > 0 || opp.compensation_range_max > 0) && (
                            <Badge variant="outline" className="text-xs">
                              💰 ${opp.compensation_range_min.toLocaleString()} - ${opp.compensation_range_max.toLocaleString()}/mo
                            </Badge>
                          )}
                          {opp.employment_type && opp.employment_type.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {opp.employment_type[0]}
                            </Badge>
                          )}
                          {opp.work_location && opp.work_location.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              📍 {opp.work_location[0]}
                            </Badge>
                          )}
                          {opp.number_of_positions && opp.number_of_positions > 1 && (
                            <Badge variant="outline" className="text-xs">
                              {opp.number_of_positions} positions
                            </Badge>
                          )}
                        </div>
                        
                        {!isOwner && opp.status === 'open' && (
                          <Button 
                            size="sm" 
                            className="mt-4 w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpressInterest(opp.id);
                            }}
                          >
                            Express Interest
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            
            {isOwner && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="text-xl font-semibold text-slate-800 mb-4">
                  Proposals ({proposals.length})
                </h3>
                
                {loadingProposals ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : proposals.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No proposals yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {proposals.map((proposal) => (
                      <Card key={proposal.id} className="bg-slate-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              {proposal.user?.picture && (
                                <img 
                                  src={proposal.user.picture} 
                                  alt={proposal.user.name}
                                  className="w-12 h-12 rounded-full"
                                />
                              )}
                              <div>
                                <h4 className="font-semibold text-slate-800">{proposal.user?.name}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  {proposal.vibe_match_percentage !== undefined && (
                                    <Badge variant="outline" className={`text-xs ${getMatchBadgeColor(proposal.vibe_match_percentage)}`}>
                                      🎯 {proposal.vibe_match_percentage}% Match
                                    </Badge>
                                  )}
                                  {proposal.average_rating && (
                                    <Badge variant="outline" className="text-xs">
                                      ⭐ {proposal.average_rating.toFixed(1)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Badge variant={
                              proposal.status === 'accepted' ? 'default' :
                              proposal.status === 'declined' ? 'secondary' :
                              'outline'
                            }>
                              {proposal.status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-3 text-sm">
                            {proposal.location && (
                              <p><strong>Location:</strong> {proposal.location}</p>
                            )}
                            
                            {proposal.skills && proposal.skills.length > 0 && (
                              <div>
                                <strong>Skills:</strong>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {proposal.skills.map((skill, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {proposal.compensation_expectation && (
                              <p><strong>Compensation:</strong> ${proposal.compensation_expectation.toLocaleString()}/month</p>
                            )}
                            
                            <div>
                              <strong>Cover Message:</strong>
                              <p className="text-slate-600 mt-1">{proposal.cover_message}</p>
                            </div>
                            
                            <div>
                              <strong>Relevant Experience:</strong>
                              <p className="text-slate-600 mt-1 whitespace-pre-wrap">{proposal.relevant_experience}</p>
                            </div>
                          </div>
                          
                          {proposal.status === 'pending' && (
                            <div className="flex gap-2 mt-4">
                              <Button 
                                size="sm" 
                                onClick={() => handleProposalStatus(proposal.id, 'accepted')}
                              >
                                Accept
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleProposalStatus(proposal.id, 'declined')}
                              >
                                Decline
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Edit Campaign Dialog */}
        {showEditCampaign && (
          <Dialog open={showEditCampaign} onOpenChange={setShowEditCampaign}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Campaign</DialogTitle>
                <DialogDescription>
                  Update your campaign details and settings
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-title">Campaign Title *</Label>
                  <Input
                    id="edit-title"
                    value={editCampaignData.title}
                    onChange={(e) => setEditCampaignData({...editCampaignData, title: e.target.value})}
                    placeholder="Campaign title"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-description">Description *</Label>
                  <Textarea
                    id="edit-description"
                    value={editCampaignData.description}
                    onChange={(e) => setEditCampaignData({...editCampaignData, description: e.target.value})}
                    placeholder="Campaign description"
                    rows={4}
                    className="mt-2"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-type">Campaign Type</Label>
                    <Select 
                      value={editCampaignData.campaign_type} 
                      onValueChange={(v) => setEditCampaignData({...editCampaignData, campaign_type: v})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="state">State</SelectItem>
                        <SelectItem value="federal">Federal</SelectItem>
                        <SelectItem value="project">Project</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-location">Location</Label>
                    <Input
                      id="edit-location"
                      value={editCampaignData.location}
                      onChange={(e) => setEditCampaignData({...editCampaignData, location: e.target.value})}
                      placeholder="Location"
                      className="mt-2"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-skills">Skills Needed</Label>
                  <div className="mt-2">
                    <SkillsSelector
                      selectedSkills={editCampaignData.skills_needed}
                      onSkillsChange={(skills) => setEditCampaignData({...editCampaignData, skills_needed: skills})}
                      placeholder="Add skills..."
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-match-person">Primary Match Person</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    This person's Vibe Check will be used to calculate match % with applicants. Defaults to you.
                  </p>
                  <div className="relative mt-2">
                    <Input
                      id="edit-match-person"
                      value={
                        userSearchTerm || 
                        (editCampaignData.primary_match_person_id === currentUser?.id 
                          ? currentUser?.name || 'You' 
                          : users.find(u => u.id === editCampaignData.primary_match_person_id)?.name || '')
                      }
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      placeholder="Search team members..."
                      className="w-full"
                    />
                    
                    {showUserDropdown && userSearchTerm && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                        {users
                          .filter(u => 
                            u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                            u.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                          )
                          .slice(0, 5)
                          .map(user => (
                            <div
                              key={user.id}
                              className="px-4 py-2 hover:bg-slate-100 cursor-pointer"
                              onClick={() => {
                                setEditCampaignData({...editCampaignData, primary_match_person_id: user.id});
                                setUserSearchTerm('');
                                setShowUserDropdown(false);
                              }}
                            >
                              <div className="font-medium">{user.name}</div>
                              <div className="text-xs text-slate-500">{user.email}</div>
                            </div>
                          ))}
                        {users.filter(u => 
                          u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                          u.email.toLowerCase().includes(userSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-2 text-slate-500">No users found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {editCampaignData.primary_match_person_id === currentUser?.id ? (
                      <span className="text-slate-600">Currently set to: <strong>You ({currentUser?.name})</strong></span>
                    ) : (
                      <span className="text-slate-600">
                        Current: <strong>{users.find(u => u.id === editCampaignData.primary_match_person_id)?.name || 'You'}</strong>
                      </span>
                    )}
                  </p>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleUpdateCampaign} className="flex-1">
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setShowEditCampaign(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}

// ============ Opportunity Detail Page ============
function OpportunityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalStep, setProposalStep] = useState(1);
  const [myProfile, setMyProfile] = useState(null);
  const [myRatings, setMyRatings] = useState(null);
  const [showEditOpportunity, setShowEditOpportunity] = useState(false);
  const [editOppData, setEditOppData] = useState({
    job_title: '',
    description: '',
    required_skills: [],
    compensation_range_min: 0,
    compensation_range_max: 5000,
    compensation_negotiable: false,
    number_of_positions: 1
  });
  
  const [proposalData, setProposalData] = useState({
    cover_message: '',
    relevant_experience: '',
    skills: [],
    location: '',
    compensation_expectation: 0
  });
  
  useEffect(() => {
    loadOpportunity();
    loadMyProfile();
  }, [id]);
  
  const loadOpportunity = async () => {
    try {
      const response = await axios.get(`${API}/opportunities/${id}`);
      setOpportunity(response.data.opportunity);
      setCampaign(response.data.campaign);
    } catch (error) {
      console.error('Opportunity load error:', error);
      toast.error('Failed to load opportunity');
    } finally {
      setLoading(false);
    }
  };
  
  const loadMyProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`);
      setMyProfile(response.data.profile);
      
      if (currentUser) {
        const ratingsResponse = await axios.get(`${API}/ratings/${currentUser.id}`);
        setMyRatings(ratingsResponse.data);
      }
    } catch (error) {
      console.error('Profile load error:', error);
    }
  };
  
  const handleExpressInterest = () => {
    if (!myProfile) {
      toast.error('Please complete your profile first');
      navigate('/profile');
      return;
    }
    
    setProposalData({
      cover_message: '',
      relevant_experience: '',
      skills: myProfile.skills || [],
      location: myProfile.location || '',
      compensation_expectation: myProfile.preferences?.compensation_range || 0
    });
    
    setProposalStep(1);
    setShowProposalModal(true);
  };
  
  const handleSubmitProposal = async () => {
    if (!proposalData.cover_message.trim() || !proposalData.relevant_experience.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/campaigns/${campaign.id}/proposals`, {
        cover_message: proposalData.cover_message,
        relevant_experience: proposalData.relevant_experience,
        opportunity_id: opportunity.id
      });
      
      toast.success('Proposal submitted successfully!');
      setShowProposalModal(false);
      setProposalStep(1);
      navigate('/my-proposals');
    } catch (error) {
      console.error('Proposal submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit proposal');
    }
  };
  
  const handleEditOpportunity = () => {
    setEditOppData({
      job_title: opportunity.job_title || '',
      description: opportunity.description || '',
      required_skills: opportunity.required_skills || [],
      compensation_range_min: opportunity.compensation_range_min || 0,
      compensation_range_max: opportunity.compensation_range_max || 5000,
      compensation_negotiable: opportunity.compensation_negotiable || false,
      number_of_positions: opportunity.number_of_positions || 1
    });
    setShowEditOpportunity(true);
  };
  
  const handleUpdateOpportunity = async () => {
    try {
      await axios.put(`${API}/opportunities/${id}`, editOppData);
      toast.success('Opportunity updated!');
      setShowEditOpportunity(false);
      loadOpportunity();
    } catch (error) {
      console.error('Update opportunity error:', error);
      toast.error('Failed to update opportunity');
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }
  
  if (!opportunity || !campaign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Opportunity Not Found</h2>
          <Button onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
        </div>
      </Layout>
    );
  }
  
  const isOwner = currentUser && campaign.creator_id === currentUser.id;
  const matchPercentage = opportunity.vibe_match_percentage || 0;
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(`/campaigns/${campaign.id}`)} className="mb-6">
          ← Back to Campaign
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div className="flex gap-2">
                {opportunity.status === 'filled' ? (
                  <Badge variant="secondary">Filled</Badge>
                ) : (
                  <Badge variant="default" className="bg-green-600">Open</Badge>
                )}
                {!isOwner && matchPercentage > 0 && (
                  <Badge variant="default" className={getMatchBadgeColor(matchPercentage)}>
                    🎯 {matchPercentage}% Match
                  </Badge>
                )}
              </div>
              {isOwner && opportunity.status === 'open' && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleEditOpportunity}
                  >
                    Edit Opportunity
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/opportunities/${id}`, { status: 'filled' });
                        toast.success('Opportunity marked as filled');
                        loadOpportunity();
                      } catch (error) {
                        toast.error('Failed to update opportunity');
                      }
                    }}
                  >
                    Mark as Filled
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this opportunity? This will also delete all associated proposals.')) {
                        try {
                          await axios.delete(`${API}/opportunities/${id}`);
                          toast.success('Opportunity deleted successfully');
                          navigate(`/campaigns/${opportunity.campaign_id}`);
                        } catch (error) {
                          console.error('Delete error:', error);
                          toast.error('Failed to delete opportunity');
                        }
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
            <CardTitle className="text-3xl mb-2">{opportunity.job_title}</CardTitle>
            <CardDescription className="text-base">
              {campaign.title} • by {campaign.creator?.name || 'Unknown'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">About This Role</h3>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                {opportunity.description}
              </p>
            </div>
            
            {(opportunity.compensation_range_min > 0 || opportunity.compensation_range_max > 0 || opportunity.compensation_negotiable) && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Compensation</h3>
                {opportunity.compensation_negotiable ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      💰 Negotiable
                    </Badge>
                  </div>
                ) : (
                  <p className="text-slate-600">
                    {opportunity.compensation_range_min > 0 || opportunity.compensation_range_max > 0 ? (
                      `$${opportunity.compensation_range_min.toLocaleString()} - $${opportunity.compensation_range_max.toLocaleString()}/month`
                    ) : (
                      'Not specified'
                    )}
                  </p>
                )}
              </div>
            )}
            
            {opportunity.required_skills && opportunity.required_skills.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Required Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {opportunity.required_skills.map((skill, idx) => (
                    <Badge key={idx} variant="outline" className="px-3 py-1">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              {opportunity.employment_type && opportunity.employment_type.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Employment Type</h3>
                  <div className="flex flex-wrap gap-2">
                    {opportunity.employment_type.map((type, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{type}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {opportunity.work_location && opportunity.work_location.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Work Location</h3>
                  <div className="flex flex-wrap gap-2">
                    {opportunity.work_location.map((loc, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{loc}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {opportunity.responsibility_level && opportunity.responsibility_level.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Responsibility Level</h3>
                  <div className="flex flex-wrap gap-2">
                    {opportunity.responsibility_level.map((level, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{level}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {opportunity.number_of_positions > 1 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Positions Available</h3>
                  <p className="text-slate-600">{opportunity.number_of_positions} openings</p>
                </div>
              )}
            </div>
            
            {opportunity.start_date && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Start Date</h3>
                <p className="text-slate-600">{opportunity.start_date}</p>
              </div>
            )}
            
            {!isOwner && opportunity.status === 'open' && (
              <div className="pt-4">
                <Button className="w-full" onClick={handleExpressInterest}>
                  Express Interest for This Role
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Proposal Modal - Same as campaign detail page */}
        <Dialog open={showProposalModal} onOpenChange={setShowProposalModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {proposalStep === 1 ? 'Review Your Proposal' : 'Submit Proposal'}
              </DialogTitle>
              <DialogDescription>
                {proposalStep === 1 
                  ? 'Review and edit your information before submitting' 
                  : 'Final step: Add your message and experience'}
              </DialogDescription>
            </DialogHeader>
            
            {proposalStep === 1 ? (
              <div className="space-y-4">
                <div>
                  <Label>Skills</Label>
                  <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 rounded-lg">
                    {proposalData.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={proposalData.location}
                    onChange={(e) => setProposalData({...proposalData, location: e.target.value})}
                    placeholder="Your location"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="compensation">Compensation Expectation (Monthly USD)</Label>
                  <div className="mt-2">
                    <Input
                      type="number"
                      id="compensation"
                      value={proposalData.compensation_expectation}
                      onChange={(e) => setProposalData({...proposalData, compensation_expectation: parseInt(e.target.value) || 0})}
                      placeholder="0"
                      min="0"
                      max="10000"
                    />
                    <p className="text-sm text-slate-500 mt-1">
                      ${proposalData.compensation_expectation.toLocaleString()}/month
                    </p>
                  </div>
                </div>
                
                {myRatings && myRatings.average > 0 && (
                  <div>
                    <Label>Your Rating</Label>
                    <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-800">
                        ⭐ {myRatings.average.toFixed(1)} / 5.0
                      </p>
                      <p className="text-sm text-slate-500">
                        Based on {myRatings.count} review{myRatings.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => setProposalStep(2)} className="flex-1">
                    Next: Add Message
                  </Button>
                  <Button variant="outline" onClick={() => setShowProposalModal(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cover_message">Cover Message *</Label>
                  <Textarea
                    id="cover_message"
                    value={proposalData.cover_message}
                    onChange={(e) => setProposalData({...proposalData, cover_message: e.target.value})}
                    placeholder="Introduce yourself and explain why you're interested in this role..."
                    rows={4}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="relevant_experience">Relevant Experience *</Label>
                  <Textarea
                    id="relevant_experience"
                    value={proposalData.relevant_experience}
                    onChange={(e) => setProposalData({...proposalData, relevant_experience: e.target.value})}
                    placeholder="Describe your relevant experience and qualifications..."
                    rows={6}
                    className="mt-2"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => setProposalStep(1)} variant="outline">
                    ← Back
                  </Button>
                  <Button onClick={handleSubmitProposal} className="flex-1">
                    Submit Proposal
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Edit Opportunity Dialog */}
        {showEditOpportunity && (
          <Dialog open={showEditOpportunity} onOpenChange={setShowEditOpportunity}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Opportunity</DialogTitle>
                <DialogDescription>Update the details for this opportunity</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-job-title">Job Title *</Label>
                  <Input
                    id="edit-job-title"
                    value={editOppData.job_title}
                    onChange={(e) => setEditOppData({...editOppData, job_title: e.target.value})}
                    className="mt-2"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-description">Description *</Label>
                  <Textarea
                    id="edit-description"
                    value={editOppData.description}
                    onChange={(e) => setEditOppData({...editOppData, description: e.target.value})}
                    rows={6}
                    className="mt-2"
                    required
                  />
                </div>
                
                <div>
                  <Label>Required Skills</Label>
                  <div className="mt-2">
                    <SkillsSelector
                      selectedSkills={editOppData.required_skills}
                      onSkillsChange={(skills) => setEditOppData({...editOppData, required_skills: skills})}
                      placeholder="Add required skills..."
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Compensation Range (Monthly USD)</Label>
                  <div className="mt-4 space-y-4">
                    <div>
                      <Slider
                        value={[editOppData.compensation_range_min, editOppData.compensation_range_max]}
                        onValueChange={(values) => setEditOppData({
                          ...editOppData, 
                          compensation_range_min: values[0], 
                          compensation_range_max: values[1]
                        })}
                        min={0}
                        max={10000}
                        step={500}
                        className="w-full"
                        disabled={editOppData.compensation_negotiable}
                      />
                      <div className="flex justify-between text-sm text-slate-600 mt-2">
                        <span>${editOppData.compensation_range_min.toLocaleString()}</span>
                        <span>${editOppData.compensation_range_max.toLocaleString()}</span>
                      </div>
                      <div className="text-center text-sm text-slate-500 mt-1">
                        ${editOppData.compensation_range_min.toLocaleString()} - ${editOppData.compensation_range_max.toLocaleString()} / month
                      </div>
                    </div>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editOppData.compensation_negotiable}
                        onChange={(e) => setEditOppData({...editOppData, compensation_negotiable: e.target.checked})}
                        className="w-4 h-4 text-slate-700 rounded focus:ring-slate-500"
                      />
                      <span className="text-sm text-slate-700">Compensation is Negotiable</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-positions">Number of Positions</Label>
                  <Input
                    type="number"
                    id="edit-positions"
                    value={editOppData.number_of_positions}
                    onChange={(e) => setEditOppData({...editOppData, number_of_positions: parseInt(e.target.value) || 1})}
                    min="1"
                    className="mt-2"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleUpdateOpportunity} className="flex-1">
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setShowEditOpportunity(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}

// ============ My Proposals Page ============
function MyProposalsPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadMyProposals();
  }, []);
  
  const loadMyProposals = async () => {
    try {
      const response = await axios.get(`${API}/proposals/my-proposals`);
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Proposals load error:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-slate-800">My Proposals</h1>
          <Button onClick={() => navigate('/campaigns')} variant="outline">
            Browse Campaigns
          </Button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : proposals.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="mb-4 text-4xl">📋</div>
              <p className="text-lg text-slate-600 mb-2">No proposals yet</p>
              <p className="text-sm text-slate-500 mb-4">
                Browse campaigns and submit proposals to get started
              </p>
              <Button onClick={() => navigate('/campaigns')}>
                Browse Campaigns
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {proposals.map((proposal) => (
              <Card key={proposal.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-800 mb-1">
                        {proposal.campaign?.title}
                      </h3>
                      <p className="text-sm text-slate-500 mb-2">
                        {proposal.opportunity_id ? (
                          <span className="text-blue-600">
                            📌 Specific Opportunity
                          </span>
                        ) : (
                          <span className="text-slate-600">
                            📋 General Campaign Proposal
                          </span>
                        )}
                        {' • '}
                        Submitted {new Date(proposal.created_at).toLocaleDateString()}
                      </p>
                      <Badge variant={
                        proposal.status === 'accepted' ? 'default' :
                        proposal.status === 'declined' ? 'secondary' :
                        'outline'
                      }>
                        {proposal.status}
                      </Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/campaigns/${proposal.campaign_id}`)}
                    >
                      View Campaign
                    </Button>
                  </div>
                  
                  <div className="space-y-3 text-sm border-t border-slate-200 pt-4">
                    {proposal.location && (
                      <p><strong>Location:</strong> {proposal.location}</p>
                    )}
                    
                    {proposal.compensation_expectation && (
                      <p><strong>Compensation:</strong> ${proposal.compensation_expectation.toLocaleString()}/month</p>
                    )}
                    
                    {proposal.skills && proposal.skills.length > 0 && (
                      <div>
                        <strong>Skills:</strong>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {proposal.skills.map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <strong>Cover Message:</strong>
                      <p className="text-slate-600 mt-1">{proposal.cover_message}</p>
                    </div>
                    
                    <div>
                      <strong>Relevant Experience:</strong>
                      <p className="text-slate-600 mt-1 whitespace-pre-wrap">{proposal.relevant_experience}</p>
                    </div>
                  </div>
                  
                  {proposal.status === 'accepted' && proposal.campaign && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        🎉 Your proposal was accepted! Contact the campaign organizer to discuss next steps.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ============ Create Opportunity Page ============
function CreateOpportunityPage() {
  const { id } = useParams(); // campaign_id
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    job_title: '',
    description: '',
    required_skills: [],
    compensation_range_min: 0,
    compensation_range_max: 5000,
    compensation_negotiable: false,
    start_date: '',
    number_of_positions: 1,
    employment_type: [],
    work_location: [],
    campaign_level: [],
    responsibility_level: [],
    hours_availability: [],
    work_culture: []
  });
  
  useEffect(() => {
    if (id) {
      loadCampaign();
    }
  }, [id]);
  
  const loadCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}`);
      setCampaign(response.data.campaign);
    } catch (error) {
      console.error('Campaign load error:', error);
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.job_title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/campaigns/${id}/opportunities`, formData);
      toast.success('Opportunity created!');
      navigate(`/campaigns/${id}`);
    } catch (error) {
      console.error('Create opportunity error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create opportunity');
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }
  
  if (!campaign) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Campaign Not Found</h2>
            <Button onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(`/campaigns/${id}`)} className="mb-6">
          ← Back to Campaign
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>Create Opportunity</CardTitle>
            <CardDescription>
              Add a new job opportunity for {campaign.title}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="job_title">Job Title *</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                  placeholder="e.g., Campaign Manager"
                  className="mt-2"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the role, responsibilities, and requirements..."
                  rows={6}
                  className="mt-2"
                  required
                />
              </div>
              
              <div>
                <Label>Required Skills</Label>
                <div className="mt-2">
                  <SkillsSelector
                    selectedSkills={formData.required_skills}
                    onSkillsChange={(skills) => setFormData({...formData, required_skills: skills})}
                    placeholder="Add required skills..."
                  />
                </div>
              </div>
              
              <div>
                <Label>Compensation Range (Monthly USD)</Label>
                <div className="mt-4 space-y-4">
                  <div>
                    <Slider
                      value={[formData.compensation_range_min, formData.compensation_range_max]}
                      onValueChange={(values) => setFormData({
                        ...formData, 
                        compensation_range_min: values[0], 
                        compensation_range_max: values[1]
                      })}
                      min={0}
                      max={10000}
                      step={500}
                      className="w-full"
                      disabled={formData.compensation_negotiable}
                    />
                    <div className="flex justify-between text-sm text-slate-600 mt-2">
                      <span>${formData.compensation_range_min.toLocaleString()}</span>
                      <span>${formData.compensation_range_max.toLocaleString()}</span>
                    </div>
                    <div className="text-center text-sm text-slate-500 mt-1">
                      ${formData.compensation_range_min.toLocaleString()} - ${formData.compensation_range_max.toLocaleString()} / month
                    </div>
                  </div>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.compensation_negotiable}
                      onChange={(e) => setFormData({...formData, compensation_negotiable: e.target.checked})}
                      className="w-4 h-4 text-slate-700 rounded focus:ring-slate-500"
                    />
                    <span className="text-sm text-slate-700">Compensation is Negotiable</span>
                  </label>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    type="date"
                    id="start_date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="positions">Number of Positions</Label>
                  <Input
                    type="number"
                    id="positions"
                    value={formData.number_of_positions}
                    onChange={(e) => setFormData({...formData, number_of_positions: parseInt(e.target.value) || 1})}
                    min="1"
                    className="mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label>Employment Type</Label>
                <div className="space-y-2 mt-2">
                  {['Full-time', 'Part-time', 'Contract', 'Volunteer'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.employment_type.includes(opt)} 
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            employment_type: e.target.checked 
                              ? [...formData.employment_type, opt] 
                              : formData.employment_type.filter(v => v !== opt)
                          });
                        }}
                        className="rounded"
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Work Location</Label>
                <div className="space-y-2 mt-2">
                  {['On-site', 'Hybrid', 'Remote', 'Flexible'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.work_location.includes(opt)} 
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            work_location: e.target.checked 
                              ? [...formData.work_location, opt] 
                              : formData.work_location.filter(v => v !== opt)
                          });
                        }}
                        className="rounded"
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Responsibility Level</Label>
                <div className="space-y-2 mt-2">
                  {['Entry-level', 'Mid-level', 'Senior-level', 'Director/Executive'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.responsibility_level.includes(opt)} 
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            responsibility_level: e.target.checked 
                              ? [...formData.responsibility_level, opt] 
                              : formData.responsibility_level.filter(v => v !== opt)
                          });
                        }}
                        className="rounded"
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  Create Opportunity
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(`/campaigns/${id}`)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

// ============ Connections Page ============
function ConnectionsPage() {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const loadConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`);
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error('Connections load error:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };
  
  const acceptConnection = async (connId) => {
    try {
      await axios.post(`${API}/connections/${connId}/accept`);
      toast.success('Connection accepted!');
      loadConnections();
    } catch (error) {
      console.error('Accept error:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept connection');
    }
  };
  
  const declineConnection = async (connId) => {
    try {
      await axios.post(`${API}/connections/${connId}/decline`);
      toast.success('Connection declined');
      loadConnections();
    } catch (error) {
      console.error('Decline error:', error);
      toast.error(error.response?.data?.detail || 'Failed to decline connection');
    }
  };
  
  // Separate incoming (can accept) and outgoing (sent by me) pending requests
  const incomingPending = connections.filter(c => c.status === 'pending' && c.recipient_id === user?.id);
  const outgoingPending = connections.filter(c => c.status === 'pending' && c.requester_id === user?.id);
  const accepted = connections.filter(c => c.status === 'accepted');
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="connections-page">
        <h1 className="text-3xl font-bold text-slate-800 mb-8">My Connections</h1>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <Tabs defaultValue="all">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">Connected ({accepted.length})</TabsTrigger>
              <TabsTrigger value="requests">Requests ({incomingPending.length})</TabsTrigger>
              <TabsTrigger value="sent">Sent ({outgoingPending.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-4 mt-6">
              {accepted.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Connections Yet</h3>
                    <p className="text-slate-600 mb-4">Start discovering people and building your network!</p>
                    <Button variant="outline" onClick={() => window.location.href = '/discover'}>
                      Discover People
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                accepted.map(conn => (
                  <Card key={conn.id} data-testid={`connection-${conn.id}`}>
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        <img 
                          src={conn.other_user?.picture || 'https://via.placeholder.com/64'}
                          alt={conn.other_user?.name}
                          className="w-16 h-16 rounded-full"
                        />
                        <div className="flex-1">
                          <CardTitle className="text-lg">{conn.other_user?.name}</CardTitle>
                          <p className="text-sm text-slate-500">{conn.other_user?.email}</p>
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {conn.other_user?.user_type}
                          </Badge>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => window.location.href = `/profile/${conn.other_user?.id}`}>
                          View Profile
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </TabsContent>
            
            <TabsContent value="requests" className="space-y-4 mt-6">
              {incomingPending.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12 text-slate-600">
                    <p>No incoming connection requests</p>
                  </CardContent>
                </Card>
              ) : (
                incomingPending.map(conn => (
                  <Card key={conn.id} data-testid={`incoming-connection-${conn.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <img 
                            src={conn.other_user?.picture || 'https://via.placeholder.com/64'}
                            alt={conn.other_user?.name}
                            className="w-16 h-16 rounded-full"
                          />
                          <div>
                            <CardTitle className="text-lg">{conn.other_user?.name}</CardTitle>
                            <p className="text-sm text-slate-500">{conn.other_user?.email}</p>
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {conn.other_user?.user_type}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => acceptConnection(conn.id)}
                            data-testid={`accept-${conn.id}`}
                          >
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => declineConnection(conn.id)}
                            data-testid={`decline-${conn.id}`}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </TabsContent>
            
            <TabsContent value="sent" className="space-y-4 mt-6">
              {outgoingPending.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12 text-slate-600">
                    <p>No pending sent requests</p>
                  </CardContent>
                </Card>
              ) : (
                outgoingPending.map(conn => (
                  <Card key={conn.id} data-testid={`outgoing-connection-${conn.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <img 
                            src={conn.other_user?.picture || 'https://via.placeholder.com/64'}
                            alt={conn.other_user?.name}
                            className="w-16 h-16 rounded-full"
                          />
                          <div>
                            <CardTitle className="text-lg">{conn.other_user?.name}</CardTitle>
                            <p className="text-sm text-slate-500">{conn.other_user?.email}</p>
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {conn.other_user?.user_type}
                            </Badge>
                          </div>
                        </div>
                        
                        <Badge variant="outline" className="text-yellow-700 border-yellow-700">
                          Pending
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
    navigate('/');
  };
  
  return <AuthContext.Provider value={{ user, loading, logout, checkAuth }}>{children}</AuthContext.Provider>;
}

// Main App
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<EmailLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin/cleanup" element={<><AuthHandler /><ProtectedRoute><AdminCleanupPage /></ProtectedRoute></>} />
          <Route path="/dashboard" element={<><AuthHandler /><ProtectedRoute><Dashboard /></ProtectedRoute></>} />
          <Route path="/vibe-check" element={<ProtectedRoute><VibeCheckPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
          <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id/opportunities/create" element={<ProtectedRoute><CreateOpportunityPage /></ProtectedRoute>} />
          <Route path="/opportunities/:id" element={<ProtectedRoute><OpportunityDetailPage /></ProtectedRoute>} />
          <Route path="/my-proposals" element={<ProtectedRoute><MyProposalsPage /></ProtectedRoute>} />
          <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;